import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  type ApiKeyAdminFieldsFragment,
  ApiKeyAdminGetDocument,
  ApiKeyAdminListDocument,
  type ApiUserIdFormat,
  type OutboundWebhookEventType,
  type WebhookPayloadShape,
  type WebhookTimestampMode,
  useApiKeyAdminUpdateMutation,
} from "../../../generated/graphql";
import { blankToNull } from "../../../utils/string";
import useEventTypeOptions from "../hooks/use-event-type-options";
import { throwOnGraphqlErrors } from "../throw-on-graphql-errors";
import AdvancedApiKeyFields from "./advanced-api-key-fields";
import StatusMappingsDialog from "./status-mappings-dialog";

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
};

type EditApiKeyDialogProps = {
  readonly open: boolean;
  readonly apiKey: ApiKeyAdminFieldsFragment;
  readonly onClose: () => void;
};

/**
 * Modal form for editing an existing API key. The `apiKey` value and `uuid`
 * are intentionally not editable — changing `apiKey` would break customer
 * integrations that are already using it.
 */
const EditApiKeyDialog = ({ open, apiKey, onClose }: EditApiKeyDialogProps) => {
  const [name, setName] = useState(apiKey.name);
  const [webhookUrl, setWebhookUrl] = useState(apiKey.webhookUrl ?? "");
  const [enableStaging, setEnableStaging] = useState(apiKey.enableStaging);
  const [headerName, setHeaderName] = useState(
    apiKey.webhookAuthHeaderName ?? "",
  );
  const [headerValue, setHeaderValue] = useState(
    apiKey.webhookAuthHeaderValue ?? "",
  );
  const [payloadShapes, setPayloadShapes] = useState<WebhookPayloadShape[]>(
    apiKey.payloadShapes,
  );
  const [eventTypes, setEventTypes] = useState<OutboundWebhookEventType[]>(
    apiKey.eventTypes,
  );
  const [idFormat, setIdFormat] = useState<ApiUserIdFormat>(apiKey.idFormat);
  const [customMapping, setCustomMapping] = useState(apiKey.customMapping);
  const [emitTerminalHandoffEvents, setEmitTerminalHandoffEvents] = useState(
    apiKey.emitTerminalHandoffEvents,
  );
  const [webhookTimestampMode, setWebhookTimestampMode] =
    useState<WebhookTimestampMode>(apiKey.webhookTimestampMode);
  const [statusMappingsOpen, setStatusMappingsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(apiKey.name);
      setWebhookUrl(apiKey.webhookUrl ?? "");
      setEnableStaging(apiKey.enableStaging);
      setHeaderName(apiKey.webhookAuthHeaderName ?? "");
      setHeaderValue(apiKey.webhookAuthHeaderValue ?? "");
      setPayloadShapes(apiKey.payloadShapes);
      setEventTypes(apiKey.eventTypes);
      setIdFormat(apiKey.idFormat);
      setCustomMapping(apiKey.customMapping);
      setEmitTerminalHandoffEvents(apiKey.emitTerminalHandoffEvents);
      setWebhookTimestampMode(apiKey.webhookTimestampMode);
      setError(null);
    }
  }, [open, apiKey]);

  const [updateKey, { loading }] = useApiKeyAdminUpdateMutation({
    refetchQueries: [ApiKeyAdminListDocument, ApiKeyAdminGetDocument],
  });

  const eventTypeOptions = useEventTypeOptions({
    payloadShapes,
    emitTerminalHandoffEvents,
    eventTypes,
  });
  const hasOutOfScopeEventTypes =
    eventTypeOptions.status === "ready" &&
    eventTypeOptions.outOfScope.length > 0;

  const handleSubmit = async () => {
    if (name.trim().length === 0) {
      setError("Name is required");
      return;
    }
    setError(null);
    try {
      const result = await updateKey({
        variables: {
          input: {
            uuid: apiKey.uuid,
            name: name.trim(),
            webhookUrl: blankToNull(webhookUrl),
            enableStaging,
            webhookAuthHeaderName: blankToNull(headerName),
            webhookAuthHeaderValue: blankToNull(headerValue),
            payloadShapes,
            eventTypes,
            idFormat,
            customMapping,
            emitTerminalHandoffEvents,
            webhookTimestampMode,
          },
        },
      });
      throwOnGraphqlErrors(result);
      onClose();
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    }
  };

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={onClose}>
      <DialogTitle>Edit API key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            required
            fullWidth
            label="Name"
            value={name}
            size="small"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <TextField
            fullWidth
            label="Webhook URL"
            value={webhookUrl}
            size="small"
            onChange={(e) => {
              setWebhookUrl(e.target.value);
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={enableStaging}
                onChange={(e) => {
                  setEnableStaging(e.target.checked);
                }}
              />
            }
            label="Enable staging webhooks"
          />
          <TextField
            fullWidth
            label="Webhook auth header name"
            value={headerName}
            size="small"
            onChange={(e) => {
              setHeaderName(e.target.value);
            }}
          />
          <TextField
            fullWidth
            label="Webhook auth header value"
            value={headerValue}
            size="small"
            type="password"
            onChange={(e) => {
              setHeaderValue(e.target.value);
            }}
          />
          <AdvancedApiKeyFields
            payloadShapes={payloadShapes}
            eventTypes={eventTypes}
            idFormat={idFormat}
            customMapping={customMapping}
            emitTerminalHandoffEvents={emitTerminalHandoffEvents}
            webhookTimestampMode={webhookTimestampMode}
            eventTypeOptions={eventTypeOptions}
            onEmitTerminalHandoffEventsChange={setEmitTerminalHandoffEvents}
            onWebhookTimestampModeChange={setWebhookTimestampMode}
            onPayloadShapesChange={setPayloadShapes}
            onEventTypesChange={setEventTypes}
            onIdFormatChange={setIdFormat}
            onCustomMappingChange={setCustomMapping}
          />
          <div>
            <Button
              variant="outlined"
              onClick={() => {
                setStatusMappingsOpen(true);
              }}
            >
              Edit status mappings
            </Button>
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={
            loading || name.trim().length === 0 || hasOutOfScopeEventTypes
          }
          variant="contained"
          onClick={handleSubmit}
        >
          {loading ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
      <StatusMappingsDialog
        open={statusMappingsOpen}
        apiKeyUuid={apiKey.uuid}
        onClose={() => {
          setStatusMappingsOpen(false);
        }}
      />
    </Dialog>
  );
};

export default EditApiKeyDialog;
